import axios from 'axios';
import { formatMoney, toMinor, toMajor } from '@/lib/region';
import { toast } from 'sonner';
import { captureException } from '@/lib/sentry';

// Phase 44.12.2 — HOTFIX for the cross-origin login outage.
//
// PROBLEM: REACT_APP_BACKEND_URL is baked into the production bundle at
// build time. When the bundle is served from a CUSTOM DOMAIN (splashpad.pro)
// but the env var points at the Emergent default host (*.emergent.host),
// every API call becomes CROSS-ORIGIN. Cookies (HttpOnly + SameSite=Lax)
// can't be set across origins, so the entire Phase 44.12 cookie auth
// silently breaks — exactly what locked everyone out on prod.
//
// FIX: when we're being served from a custom domain (any HTTPS origin that
// is NOT localhost), prefer SAME-ORIGIN api calls (`/api/...`). The
// custom domain's reverse proxy at `splashpad.pro/api/*` already routes
// to the backend correctly (verified via curl). Same-origin = cookies
// work + Authorization headers work + no CORS dance + no third-party
// cookie blocking by Safari/Chrome ITP.
//
// We still use the env var verbatim during LOCAL development (when the
// frontend is on http://localhost:3000 and the backend on :8001 — those
// must be cross-origin because there's no proxy locally).
function _resolveApiBase() {
    const envBase = process.env.REACT_APP_BACKEND_URL || '';
    // SSR / build-time safety: if no window, fall back to env var.
    if (typeof window === 'undefined') return `${envBase}/api`;
    const here = window.location;
    const isLocalDev = (
        here.hostname === 'localhost' ||
        here.hostname === '127.0.0.1' ||
        here.hostname.endsWith('.local')
    );
    if (isLocalDev) return `${envBase}/api`;
    try {
        const envHost = envBase ? new URL(envBase).host : '';
        // If the env-var host matches the current origin, just use it.
        if (envHost && envHost === here.host) return `${envBase}/api`;
    } catch (e) {
        // Malformed env var — fall through to same-origin
    }
    // We're deployed on a custom domain that differs from the env's host.
    // Use same-origin via the reverse proxy. This is the SplashPad
    // production path: frontend on splashpad.pro, /api/* proxied to the
    // backend by the platform's ingress. Same-origin makes cookies +
    // Authorization headers + CORS all "just work".
    return `${here.origin}/api`;
}

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = _resolveApiBase();

// Phase 44.12 — withCredentials makes axios attach the httpOnly `sd_session`
// cookie on every request (and accept any Set-Cookie the server returns).
// Combined with the backend's allow_credentials CORS config, this is how
// our cookie-based auth flows. Existing localStorage tokens still work
// via the Authorization header during the rollout (defense in depth).
const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Phase 120 — collapse duplicate "billing locked" toasts. The response
// interceptor below shows ONE toast per 402 billing_locked reply, but most
// call sites ALSO run `toast.error(e?.response?.data?.detail || ...)`, which
// stacked an identical second toast. Any toast.error whose message equals
// the last billing-locked detail is routed through the same sonner id, so
// sonner UPDATES the existing toast instead of adding another.
const BILLING_LOCK_TOAST_ID = 'billing-locked';
let _lastBillingLockDetail = null;
try {
    const _origToastError = toast.error;
    toast.error = (message, opts) => {
        if (typeof message === 'string' && _lastBillingLockDetail && message === _lastBillingLockDetail) {
            return _origToastError(message, { ...(opts || {}), id: BILLING_LOCK_TOAST_ID, duration: 6000 });
        }
        return _origToastError(message, opts);
    };
} catch { /* sonner shape changed — fall back to default behaviour */ }

// === Safe localStorage helpers ===
// iOS Safari in Private/Locked-down Browsing throws on setItem (QuotaExceededError).
// If that happens we degrade gracefully to in-memory storage so the session survives
// the current page lifecycle, and surface a one-time warning so the user knows.
const memoryStore = {};
let _privateWarned = false;
function lsGet(key) {
    try {
        const v = localStorage.getItem(key);
        return v !== null ? v : (memoryStore[key] ?? null);
    } catch (e) {
        return memoryStore[key] ?? null;
    }
}
function lsSet(key, value) {
    memoryStore[key] = value;
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        if (!_privateWarned && typeof window !== 'undefined') {
            _privateWarned = true;
            // eslint-disable-next-line no-console
            console.warn('[SplashPad] localStorage unavailable — falling back to in-memory session. Most likely cause: Safari Private Browsing or Locked-down Browsing.');
        }
    }
}
function lsRemove(key) {
    delete memoryStore[key];
    try {
        localStorage.removeItem(key);
    } catch (err) {
        /* Safari Private mode can also fail removeItem — safe to swallow since
           memoryStore was already cleared above. Log in dev for visibility. */
        if (process.env.NODE_ENV !== 'production') console.debug('lsRemove failed (storage blocked):', err);
    }
}

api.interceptors.request.use((config) => {
    // Phase 44.12 — Authorization header is now LEGACY but still sent if a
    // token is in localStorage. Pre-Phase-44.12 users will already have a
    // token there; they'll keep working until their next login at which
    // point the server's Set-Cookie response will switch them to cookie
    // mode. New users never hit this code path (we no longer call setAuth
    // with a token).
    const reportCurrency = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('report_currency');
    if (reportCurrency && /\/instructor\/(books|insights)\//.test(config.url || '')) config.params = { ...(config.params || {}), currency: reportCurrency };
    const token = lsGet('sd_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Always include the trusted-device token (when present) so the server
    // can skip the 2FA challenge on subsequent logins from this browser.
    const dev = lsGet('sd_device_token');
    if (dev && config.url && (config.url.startsWith('/auth/login') || config.url.endsWith('/auth/login'))) {
        // axios mutates `data` for POST; merge instead of overwrite.
        try {
            const d = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
            if (!d.device_token) d.device_token = dev;
            config.data = d;
        } catch { /* if payload isn't JSON, just leave it alone */ }
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;

        // Phase 0br (beautification pack, Part 3) — no response at all means
        // the request never reached the server (offline, DNS, proxy down,
        // long timeout). Show ONE clear red toast, deduped by id, so we
        // don't stack a wall of them on a broken network.
        if (!err.response && typeof window !== 'undefined') {
            try {
                toast.error(
                    "Can't reach SplashPad right now — check your internet connection and try again. If this keeps happening, screenshot this and message us in the support chat.",
                    { id: 'sp-network-error', duration: 8000 },
                );
            } catch { /* toaster not mounted yet — ignore */ }
        }

        // Phase 74d (July 2026, Alexis import bug) — normalize Pydantic
        // 422 validation errors into a HUMAN-READABLE STRING before any
        // call site sees them.
        //
        // FastAPI emits `{ detail: [{ type, loc, msg, input, ctx }, ...] }`
        // on validation failures. If a call site does the reasonable-
        // looking `toast.error(e?.response?.data?.detail || 'Failed')`,
        // React tries to render the RAW array as a child and dies with
        // React error #31 ("Objects are not valid as a React child").
        //
        // We fix this once, here, so the 133 existing `toast.error(...)`
        // sites all become safe without needing individual edits.
        try {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                const parts = detail
                    .map((d) => {
                        if (!d || typeof d !== 'object') return String(d);
                        // FastAPI's `loc` is like ["body", "families", 0, "email"] —
                        // strip the leading "body" and turn the rest into a dotted
                        // path so the user sees which field is wrong.
                        const locArr = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body') : [];
                        const field = locArr.length ? locArr.join('.') : 'field';
                        const msg = d.msg || d.type || 'invalid value';
                        return `${field}: ${msg}`;
                    })
                    .filter(Boolean);
                if (parts.length) {
                    err.response.data.detail = parts.join(' · ');
                }
            } else if (detail && typeof detail === 'object') {
                // Single-object detail (rare — some legacy 400s). Prefer
                // its `msg` if present, else JSON-stringify as a last
                // resort so at least SOMETHING renders.
                //
                // Phase 125 — lift a machine-readable `code` (e.g.
                // `student_cap`) onto the response body before flattening so
                // feature code can branch on it without string-matching.
                if (detail.code && !err.response.data.code) err.response.data.code = detail.code;
                if (!err.response.data.detail_meta) err.response.data.detail_meta = { ...detail };
                err.response.data.detail =
                    detail.msg
                    || detail.message
                    || detail.error
                    || JSON.stringify(detail);
            }
        } catch { /* never break the interceptor over error-shaping */ }

        // Phase 125 — Starter student-cap gate (coach-side only). The backend
        // answers 402 with code=student_cap when approving / enrolling would
        // push a Starter coach past 40 active students. Show ONE actionable
        // toast with an "Upgrade to Pro" button; the call site's own
        // toast.error(detail) collapses into it via the patched toast.error.
        if (status === 402 && err?.response?.data?.code === 'student_cap' && typeof window !== 'undefined') {
            try {
                const d = err.response.data;
                const msg = d.detail || 'You have reached the 40 student limit for the Starter plan.';
                _lastBillingLockDetail = msg;
                toast.error(msg, {
                    id: BILLING_LOCK_TOAST_ID,
                    duration: 9000,
                    description: 'Parents can still sign up — their applications are saved. Approving and enrolling resumes the moment you upgrade.',
                    action: {
                        label: 'Upgrade to Pro',
                        onClick: () => { window.location.assign(d.detail_meta?.upgrade_url || '/instructor/billing'); },
                    },
                });
            } catch { /* never break the interceptor */ }
        }

        // Phase 120 — billing dunning gate. The backend answers 402 with
        // code=billing_locked when a soft-locked coach attempts a write or a
        // hard-locked coach attempts anything outside Billing. One deduped
        // toast; hard lock also sends them to the Billing page.
        if (status === 402 && err?.response?.data?.code === 'billing_locked' && typeof window !== 'undefined') {
            try {
                const d = err.response.data;
                const msg = d.detail || 'Your account is locked for non-payment.';
                const onBilling = window.location.pathname.startsWith('/instructor/billing');
                // Remember the exact detail so the patched toast.error above
                // collapses the call site's own toast into this one.
                _lastBillingLockDetail = msg;
                // Hard lock + already on Billing: the BillingLockNotice is on
                // screen and every stray background GET is 402'd by design —
                // a toast here would just be noise. Skip it.
                const suppress = d.state === 'hard_lock' && onBilling;
                // Throttle: background pollers may hit the gate too; one toast
                // per 8s is plenty (the layout banner is the persistent cue).
                const now = Date.now();
                if (!suppress && (!window.__sdBillingToastAt || now - window.__sdBillingToastAt > 8000)) {
                    window.__sdBillingToastAt = now;
                    toast.error(msg, { id: BILLING_LOCK_TOAST_ID, duration: 6000 });
                }
                if (d.state === 'hard_lock' && !onBilling && window.location.pathname.startsWith('/instructor')) {
                    window.location.assign(d.billing_url || '/instructor/billing');
                }
            } catch { /* never break the interceptor */ }
        }

        if (status === 401) {
            const path = window.location.pathname;
            if (!path.startsWith('/login') && !path.startsWith('/signup') && !path.startsWith('/coach/')) {
                lsRemove('sd_token');
                lsRemove('sd_user');
            }
        }
        // Report unexpected backend errors to Sentry. We skip:
        //  - 401/403 (auth issues — normal flow when tokens expire)
        //  - 404 (often expected: "no such waiver yet", etc.)
        //  - 422 (Pydantic validation — surfaced to user in UI)
        //  - network errors with no response (could be offline / preflight)
        // Anything 4xx outside that list OR any 5xx becomes a Sentry issue
        // so we hear about API regressions immediately.
        if (status && status !== 401 && status !== 403 && status !== 404 && status !== 422) {
            try {
                const path = err?.config?.url || '(unknown)';
                const method = (err?.config?.method || 'get').toUpperCase();
                captureException(err, {
                    tags: { module: 'api', http_status: String(status), http_method: method },
                    extra: { path },
                });
            } catch { /* never break a request over an observability call */ }
        }
        return Promise.reject(err);
    }
);

export default api;

// ---------------------------------------------------------------------------
// Phase 125 — Authenticated file downloads (CSV / PDF / ZIP).
//
// PRODUCTION BUG (Nov 2026, coach report): "I tried exporting a CSV of the
// families, but the file had nothing in it and was only 26 bytes." The file
// contained `{"detail":"Invalid token"}` — exactly 26 bytes.
//
// ROOT CAUSE: several export buttons still did a RAW `fetch()` with
// `Authorization: Bearer ${localStorage.sd_token}`. After the Phase 44.12
// cookie migration `sd_token` is NULL for every fresh login, so the request
// went out as `Bearer null` with no session cookie (`fetch` doesn't send
// cookies cross-origin without `credentials: 'include'`). The backend
// answered 401 JSON and the code blindly saved that body as `.csv`.
//
// FIX: one shared helper that goes through the axios instance (cookies +
// legacy header both attached), asks for a blob, REFUSES to save anything
// that looks like a JSON error, and surfaces the server's `detail` so the
// caller can toast it. Every export in the app should use this.
// ---------------------------------------------------------------------------
async function _blobToText(blob) {
    try {
        if (blob && typeof blob.text === 'function') return await blob.text();
    } catch { /* fall through */ }
    return '';
}

/**
 * Save a Blob to disk via a temporary anchor. Revokes the object URL after
 * a short delay (some browsers race on `click()`).
 */
export function saveBlobToFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => { try { window.URL.revokeObjectURL(url); } catch { /* ignore */ } }, 1000);
}

/**
 * Download an authenticated file from the API and save it.
 *
 * @param {string} path      API path relative to API_BASE, e.g. `/instructor/students/export-csv`
 * @param {object} [opts]
 * @param {string} [opts.filename]  Name to save as. Falls back to the server's
 *                                  Content-Disposition filename, then `download`.
 * @param {object} [opts.params]    Query-string params.
 * @param {string} [opts.expectType] Substring the response Content-Type must
 *                                   contain (e.g. `text/csv`, `application/pdf`).
 *                                   When omitted we only reject JSON bodies.
 * @returns {Promise<{ filename: string, size: number }>}
 * @throws Error with a human-readable `.message` (server `detail` when available).
 */
export async function downloadAuthedFile(path, opts = {}) {
    const { filename, params, expectType } = opts;
    let res;
    try {
        // validateStatus: let us inspect non-2xx bodies ourselves so we can
        // pull the JSON `detail` out of the blob instead of a generic error.
        res = await api.get(path, { responseType: 'blob', params, validateStatus: () => true });
    } catch (e) {
        throw new Error(e?.message || 'Download failed — check your connection and try again.');
    }
    const status = res.status;
    const ctype = String(res.headers?.['content-type'] || res.data?.type || '').toLowerCase();
    const blob = res.data;

    // 1. Non-2xx → parse the JSON error body and surface its `detail`.
    if (status < 200 || status >= 300) {
        let detail = '';
        const txt = await _blobToText(blob);
        try { detail = JSON.parse(txt)?.detail || ''; } catch { detail = txt.slice(0, 200); }
        if (typeof detail !== 'string') detail = JSON.stringify(detail);
        if (status === 401) detail = detail || 'Your session has expired. Please log in again and retry the download.';
        throw new Error(detail || `Download failed (HTTP ${status}).`);
    }
    // 2. 2xx but the body is JSON (or otherwise the wrong type) → refuse to
    //    save a broken file. This is the exact "26-byte CSV" failure mode.
    if (ctype.includes('application/json') || (expectType && !ctype.includes(expectType))) {
        const txt = await _blobToText(blob);
        let detail = '';
        try { detail = JSON.parse(txt)?.detail || ''; } catch { /* not JSON */ }
        throw new Error(detail || 'The server returned an unexpected response instead of the file. Please try again.');
    }
    if (!blob || blob.size === 0) {
        throw new Error('The export came back empty. Please try again.');
    }

    // 3. Resolve a filename: caller → Content-Disposition → generic.
    let name = filename || '';
    if (!name) {
        const cd = String(res.headers?.['content-disposition'] || '');
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        if (m && m[1]) { try { name = decodeURIComponent(m[1]); } catch { name = m[1]; } }
    }
    if (!name) name = 'download';

    saveBlobToFile(blob, name);
    return { filename: name, size: blob.size };
}

export function setAuth(token, user) {
    // Phase 0az (Security hardening, July 2026) — we no longer write the
    // session token to localStorage. It is now delivered exclusively via
    // the httpOnly `sd_session` cookie set by the backend on /auth/login.
    // Rationale:
    //   - localStorage is readable by any JavaScript running in the page,
    //     which means a single XSS injection could exfiltrate the token
    //     and impersonate the user. The httpOnly cookie is completely
    //     unreadable by JS (defense in depth).
    //   - We still cache the *non-sensitive* user metadata (`sd_user`:
    //     display name / role / ids) in localStorage so the UI can
    //     render immediately on next load without a `/auth/me` round-
    //     trip. Nothing sensitive lives there.
    //   - Legacy tokens already in localStorage keep working via the
    //     read path in getAuth()/interceptor below until they expire or
    //     the user logs in again (at which point the token slot is wiped).
    //     After all legacy JWTs expire (max ~30 days for remember-me),
    //     the read path can be removed entirely.
    if (token) {
        // Explicitly wipe any legacy sd_token so a fresh login can't leave
        // stale token debris behind for an attacker to grab post-logout.
        try { lsRemove('sd_token'); } catch { /* ignore */ }
    }
    if (user) {
        try { lsSet('sd_user', JSON.stringify(user)); } catch { /* ignore */ }
    }
}
export function getAuth() {
    // Phase 44.12 — there is no longer a JS-readable session token by
    // design. We only return the cached user metadata. The actual auth
    // proof lives in the httpOnly cookie. Returning `token: null` keeps
    // the legacy shape so AuthProvider doesn't need a refactor (it just
    // treats null as "we don't have a local cache — defer to /auth/me").
    // We still surface any LEGACY pre-44.12 token sitting in localStorage
    // so a freshly loaded tab from before the migration keeps working
    // until the user next logs in (at which point setAuth above wipes it).
    const legacyToken = lsGet('sd_token') || null;
    const user = lsGet('sd_user');
    let parsedUser = null;
    if (user) {
        try { parsedUser = JSON.parse(user); } catch (e) { parsedUser = null; }
    }
    return { token: legacyToken, user: parsedUser };
}
export function clearAuth() {
    lsRemove('sd_token');
    lsRemove('sd_user');
}

// === Trusted-device token (2FA "remember this browser") ===
// Stored separately from the session token so it survives a logout. The
// server treats it as opaque proof that this browser has previously
// completed a second-factor challenge for the same user. Cleared from
// Settings → Trusted devices → Revoke, never automatically.
export function setDeviceToken(token, expiresAt) {
    if (!token) return;
    lsSet('sd_device_token', token);
    if (expiresAt) lsSet('sd_device_token_expires', expiresAt);
}
export function getDeviceToken() {
    return lsGet('sd_device_token') || null;
}
export function clearDeviceToken() {
    lsRemove('sd_device_token');
    lsRemove('sd_device_token_expires');
}

export function fmtPrice(cents, currency = 'usd') {
    return formatMoney(cents, currency, typeof document === 'undefined' ? 'en' : document.documentElement.lang);
}
export function currentSchoolCurrency() {
    if (typeof window !== 'undefined' && /^\/(coach|parent)(\/|$)/.test(window.location.pathname)) return document.documentElement.dataset.schoolCurrency || 'usd';
    return getAuth()?.user?.instructor_profile?.operating_currency || 'usd';
}
export function inputMoneyToMinor(value, currency) {
    return toMinor(value, currency || getAuth()?.user?.instructor_profile?.operating_currency || 'usd');
}
export function inputMoneyFromMinor(value, currency) {
    return toMajor(value, currency || getAuth()?.user?.instructor_profile?.operating_currency || 'usd');
}
export function fmtDate(iso) {
    if (!iso) return '—';
    try {
        // Phase 73y (2026-07) — date-only strings ("YYYY-MM-DD") must be
        // rendered in LOCAL time, not coerced through UTC. Coach report:
        // "I picked Jul 8 for today's attendance but the record shows
        // Jul 7." Root cause: `new Date("2026-07-08")` per the JS spec
        // parses date-only strings as UTC midnight, which
        // `toLocaleDateString` then renders in the browser's local zone.
        // For any user in a western timezone (US/Canada/Mexico/LATAM),
        // UTC midnight becomes the PREVIOUS day locally. We fix this by
        // parsing the date parts manually so the resulting Date object
        // is in LOCAL midnight of the picked day.
        if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            const [y, m, d] = iso.split('-').map(Number);
            return new Date(y, m - 1, d).toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
            });
        }
        return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return iso; }
}

/**
 * Phase 73y (2026-07) — "today" as a local YYYY-MM-DD string.
 *
 * Coaches in western timezones lose calendar days when using
 * `new Date().toISOString().slice(0, 10)` — that gives the UTC date,
 * which flips to tomorrow after ~7pm local time in Houston (or any
 * time after ~4pm PDT). Use this helper anywhere the user's LOCAL
 * calendar day matters (attendance dates, timeclock rows, "today"
 * range filters, date-picker defaults).
 *
 * @returns {string} e.g. "2026-07-08" (local date, no time)
 */
export function todayLocalISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Phase 73y (2026-07) — convert a Date to a local YYYY-MM-DD string.
 * Same rationale as {@link todayLocalISO}, but for arbitrary Date
 * objects (e.g. shift dates, schedule range boundaries).
 *
 * @param {Date} d
 * @returns {string} YYYY-MM-DD in the browser's local timezone
 */
export function dateToLocalISO(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
export function fmtDateTime(iso, tz) {
    if (!iso) return '—';
    // Phase 44.9 — force 12h AM/PM display regardless of browser locale.
    // Janelle: "we do not want military time anywhere on any portion of
    // the website." Locale-dependent rendering could surface 24h on
    // non-US browsers; pinning hour12:true keeps things consistent.
    // Phase 112 — optional IANA `tz` renders the timestamp in a specific
    // zone (parents see lesson times in their COACH's time zone).
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true,
            ...(tz ? { timeZone: tz } : {}),
        });
    } catch { return iso; }
}

/**
 * fmtTime12h — convert a 24h "HH:MM" string to "h:mm AM/PM" for display.
 * Returns the input unchanged if it doesn't look like HH:MM.
 * Examples:  "09:00" -> "9:00 AM"   "23:00" -> "11:00 PM"
 * Top-of-hour is rendered without minutes: "16:00" -> "4 PM".
 */
export function fmtTime12h(hhmm) {
    if (!hhmm || typeof hhmm !== 'string' || !/^\d{1,2}:\d{2}(?::\d{2})?$/.test(hhmm)) {
        return hhmm || '';
    }
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = (h % 12) || 12;
    return m === 0 ? `${hour12} ${period}` : `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
}

/**
 * fmtTime12hLong — like fmtTime12h but ALWAYS shows minutes ("9:00 AM"
 * not "9 AM"). Use this in dense lists / tables where consistent column
 * width matters more than terseness.
 */
export function fmtTime12hLong(hhmm) {
    if (!hhmm || typeof hhmm !== 'string' || !/^\d{1,2}:\d{2}(?::\d{2})?$/.test(hhmm)) {
        return hhmm || '';
    }
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = (h % 12) || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
}

/**
 * Derive the effective "sessions per week" for a package.
 *
 * A single `class_slots[]` entry of type `weekly` with `weekdays=[1,4]`
 * (Mon + Thu at the same time) counts as 2 sessions per week. Multiple
 * weekly slots at different times sum together. Weekly slots with
 * empty/invalid `weekdays` and `one_off` slots contribute nothing.
 *
 * Falls back to `pkg.sessions_per_week` (the coach-set default field)
 * when the package has no weekly class_slots — matches the backend
 * `compute_sessions_per_week_from_slots` helper.
 *
 * Why this lives on the frontend too: for existing packages, the DB row
 * still has the old `sessions_per_week` value (usually 1). The backend
 * auto-derives on next save, but until the coach re-saves, the parent-
 * facing display would be stale. This helper closes the gap by deriving
 * at render time from the class_slots the coach already picked.
 */
export function derivePackageSessionsPerWeek(pkg) {
    if (!pkg || typeof pkg !== 'object') return 0;
    const slots = Array.isArray(pkg.class_slots) ? pkg.class_slots : [];
    let total = 0;
    for (const s of slots) {
        if (!s || typeof s !== 'object') continue;
        if (s.type !== 'weekly') continue;
        const weekdays = Array.isArray(s.weekdays) ? s.weekdays : null;
        if (weekdays) {
            const seen = new Set();
            for (const w of weekdays) {
                const wi = Number(w);
                if (Number.isInteger(wi) && wi >= 0 && wi <= 6 && !seen.has(wi)) {
                    seen.add(wi);
                    total += 1;
                }
            }
        } else if (typeof s.weekday === 'number' && s.weekday >= 0 && s.weekday <= 6) {
            // Legacy single-weekday shape (pre-Phase 63)
            total += 1;
        }
    }
    if (total > 0) return total;
    // Fall back to the stored field for packages without weekly slots.
    const stored = Number(pkg.sessions_per_week);
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
}

/**
 * Phase 74c (2026-08) — per-SLOT sessions-per-week.
 *
 * A parent picks ONE class_slot when they enroll, so a package with two
 * separate weekly slots (e.g. 5:00pm Mon-Thu AND 5:40pm Mon-Thu) should
 * show "4x/week" per enrollment, not "8x/week" (which is what
 * `derivePackageSessionsPerWeek` returns — that's the total across all
 * slots for coach-side accounting).
 *
 * Prefers the backend-computed `sessions_per_week_per_slot` field
 * (populated by `enrich_package_for_display`) when present. Falls back
 * to computing the max weekday count across slots.
 */
export function derivePackageSessionsPerWeekPerSlot(pkg) {
    if (!pkg || typeof pkg !== 'object') return 0;
    // Prefer backend-computed value if it made it into this payload.
    const server = Number(pkg.sessions_per_week_per_slot);
    if (Number.isFinite(server) && server > 0) return server;
    const slots = Array.isArray(pkg.class_slots) ? pkg.class_slots : [];
    let best = 0;
    for (const s of slots) {
        if (!s || typeof s !== 'object' || s.type !== 'weekly') continue;
        let count = 0;
        const weekdays = Array.isArray(s.weekdays) ? s.weekdays : null;
        if (weekdays) {
            const seen = new Set();
            for (const w of weekdays) {
                const wi = Number(w);
                if (Number.isInteger(wi) && wi >= 0 && wi <= 6 && !seen.has(wi)) {
                    seen.add(wi);
                    count += 1;
                }
            }
        } else if (typeof s.weekday === 'number' && s.weekday >= 0 && s.weekday <= 6) {
            count = 1;
        }
        if (count > best) best = count;
    }
    if (best > 0) return best;
    // Fall back to the whole-package figure (also stale for legacy rows).
    return derivePackageSessionsPerWeek(pkg);
}

/**
 * Phase 74c (2026-08) — per-SLOT session count.
 *
 * For a package with multiple time-slot options, the parent's ONE
 * enrollment covers only their picked slot. We return either the
 * backend-computed `session_count_per_slot`, or fall back to
 * dividing the aggregate by the number of slots (client-side
 * approximation for legacy DB rows).
 */
export function derivePackageSessionCountPerSlot(pkg) {
    if (!pkg || typeof pkg !== 'object') return 0;
    const server = Number(pkg.session_count_per_slot);
    if (Number.isFinite(server) && server > 0) return server;
    const total = Number(pkg.session_count);
    const slots = Array.isArray(pkg.class_slots) ? pkg.class_slots : [];
    const weeklyCount = slots.filter((s) => s && s.type === 'weekly').length;
    if (Number.isFinite(total) && total > 0 && weeklyCount > 1) {
        return Math.max(1, Math.round(total / weeklyCount));
    }
    return Number.isFinite(total) && total > 0 ? total : 0;
}

/**
 * Phase 74c — how many distinct time-slot OPTIONS a package offers.
 * Used to decide whether to render "Choose from N time slots".
 */
export function derivePackageClassSlotOptionCount(pkg) {
    if (!pkg || typeof pkg !== 'object') return 0;
    const server = Number(pkg.class_slot_option_count);
    if (Number.isFinite(server) && server >= 0) return server;
    const slots = Array.isArray(pkg.class_slots) ? pkg.class_slots : [];
    return slots.filter((s) => s && s.type === 'weekly').length;
}

